import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { returnsAPI } from '../../api/returns';
import {
    ArrowLeft, Package, CheckCircle, Clock, XCircle, AlertCircle,
    Download, Truck, QrCode,
} from 'lucide-react';
import { formatDate, formatDateTimeEnglish } from '../../utils/date';
import { formatPrice } from '../../utils/utilityFunctions';
import toast from '../../utils/toast';
import type { ReturnLabel, ReturnRequest } from '../../types';

const STATUS_STEPS: Array<{ key: string; label: string }> = [
    { key: 'requested', label: 'Requested' },
    { key: 'approved', label: 'Approved' },
    { key: 'in_transit', label: 'Pickup' },
    { key: 'completed', label: 'Completed' },
];

const ReturnDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [r, setR] = useState<ReturnRequest | null>(null);
    const [label, setLabel] = useState<ReturnLabel | null>(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                setR(await returnsAPI.detail(id));
            } catch {
                toast.error('Return not found');
                navigate('/returns');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    const loadLabel = async () => {
        if (!r || label) return;
        try {
            setLabel(await returnsAPI.label(r.id));
        } catch {
            toast.error('Label not available yet');
        }
    };

    const handleCancel = async () => {
        if (!r) return;
        if (!window.confirm('Cancel this return request?')) return;
        setCancelling(true);
        try {
            setR(await returnsAPI.cancel(r.id));
            toast.success('Return cancelled');
        } catch (e: any) {
            toast.error(e?.response?.data?.error || 'Failed to cancel');
        } finally {
            setCancelling(false);
        }
    };

    if (loading || !r) {
        return (
            <div>
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                    </div>
                </div>
            </div>
        );
    }

    const currentStepIndex = (() => {
        if (['rejected', 'cancelled', 'failed'].includes(r.status)) return -1;
        if (r.status === 'completed') return 3;
        if (r.status === 'in_transit') return 2;
        if (['approved', 'partially_approved', 'auto_approved'].includes(r.status)) return 1;
        return 0;
    })();

    const isCancellable = ['pending', 'approved', 'partially_approved', 'auto_approved'].includes(r.status);
    const canShowLabel = ['approved', 'partially_approved', 'auto_approved', 'in_transit'].includes(r.status);

    return (
        <div>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <button
                    onClick={() => navigate('/returns')}
                    className="flex items-center text-blue-600 hover:text-blue-700 font-medium mb-4"
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back to Returns
                </button>

                {/* Header */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                            {r.product_image ? (
                                <img src={r.product_image} alt={r.product_name} className="w-full h-full object-cover" />
                            ) : (
                                <Package className="w-7 h-7 text-gray-400" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl font-bold text-gray-900 truncate">
                                {r.product_name}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Order #{r.order_number} · <span className="capitalize">{r.kind}</span>
                            </p>
                            {r.return_code && (
                                <p className="text-xs font-mono text-gray-600 mt-1">
                                    {r.return_code}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Status timeline */}
                {currentStepIndex >= 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <h2 className="font-semibold text-gray-900 mb-4">Status</h2>
                        <div className="grid grid-cols-4 gap-2">
                            {STATUS_STEPS.map((step, idx) => {
                                const done = idx <= currentStepIndex;
                                return (
                                    <div key={step.key} className="flex flex-col items-center">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${done ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
                                            }`}>
                                            {done ? <CheckCircle className="w-4 h-4" /> : <span className="text-sm">{idx + 1}</span>}
                                        </div>
                                        <span className={`text-xs text-center ${done ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-sm text-gray-500 mt-4">
                            Requested {formatDateTimeEnglish(r.created_at)}
                        </p>
                        {r.status === 'pending' && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3 mt-4">
                                <Clock className="w-3.5 h-3.5 inline mr-1" />
                                The vendor has until {formatDateTimeEnglish(r.sla_deadline)} to respond.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className={`rounded-2xl border p-6 mb-6 ${r.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                        }`}>
                        <div className="flex items-start gap-3">
                            <XCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${r.status === 'rejected' ? 'text-red-600' : 'text-gray-500'
                                }`} />
                            <div>
                                <p className="font-medium text-gray-900 capitalize">
                                    Return {r.status.replace('_', ' ')}
                                </p>
                                {r.vendor_notes && (
                                    <p className="text-sm text-gray-700 mt-1">{r.vendor_notes}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Approved amount */}
                {(r.approved_amount || r.item_refund_amount) && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <h2 className="font-semibold text-gray-900 mb-3">
                            {r.kind === 'refund' ? 'Refund' : 'Replacement'}
                        </h2>
                        {r.approved_amount ? (
                            <>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatPrice(r.approved_amount)}
                                </p>
                                {r.approved_quantity && r.approved_quantity !== r.requested_quantity && (
                                    <p className="text-sm text-gray-500 mt-1">
                                        For {r.approved_quantity} of {r.requested_quantity} item(s)
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-gray-600">
                                Requested: {formatPrice(r.item_refund_amount)}
                            </p>
                        )}
                        {r.return_courier_fee && parseFloat(r.return_courier_fee) > 0 && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Return courier fee</span>
                                    <span className="font-medium">
                                        {formatPrice(r.return_courier_fee)}
                                        {r.return_courier_paid_by === 'vendor' && (
                                            <span className="text-xs text-green-700 ml-2">paid by vendor</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Reason */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                    <h2 className="font-semibold text-gray-900 mb-3">Your reason</h2>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.reason}</p>
                </div>

                {/* Evidence */}
                {r.evidence && r.evidence.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <h2 className="font-semibold text-gray-900 mb-3">Evidence</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {r.evidence.map(ev => (
                                <a
                                    key={ev.id}
                                    href={ev.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block aspect-square bg-gray-100 rounded-lg overflow-hidden hover:opacity-80"
                                >
                                    {ev.kind === 'image' ? (
                                        <img src={ev.url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <video src={ev.url} className="w-full h-full object-cover" />
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Label / QR */}
                {canShowLabel && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="font-semibold text-gray-900 mb-1">Return label</h2>
                                <p className="text-sm text-gray-500">
                                    Attach this to the package. Show the code to the driver.
                                </p>
                            </div>
                            <button
                                onClick={loadLabel}
                                className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 whitespace-nowrap"
                            >
                                <Download className="w-4 h-4" />
                                Get
                            </button>
                        </div>
                        {label && (
                            <div className="mt-4 flex flex-col sm:flex-row items-center gap-4 p-4 bg-gray-50 rounded-lg">
                                <img
                                    src={`data:image/png;base64,${label.qr_png_base64}`}
                                    alt="QR"
                                    className="w-32 h-32 bg-white rounded"
                                />
                                <div className="flex-1 text-center sm:text-left">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Return code</p>
                                    <p className="text-xl font-mono font-bold text-gray-900 mt-1">
                                        {label.return_code}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-2">
                                        {label.item_summary}
                                    </p>
                                    {label.warehouse && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Return to: {label.warehouse}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-3">
                                        Write this code clearly on the outside of the package, or show the QR to the driver.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Vendor notes */}
                {r.vendor_notes && r.status !== 'rejected' && (
                    <div className="bg-blue-50 rounded-2xl border border-blue-100 p-6 mb-6">
                        <h2 className="font-semibold text-gray-900 mb-2">Vendor notes</h2>
                        <p className="text-sm text-gray-700">{r.vendor_notes}</p>
                    </div>
                )}

                {/* Cancel */}
                {isCancellable && (
                    <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="w-full py-3 border border-red-300 text-red-600 font-medium rounded-xl hover:bg-red-50 disabled:opacity-50"
                    >
                        {cancelling ? 'Cancelling…' : 'Cancel Return'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ReturnDetail;